import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  createConnector,
  getConnector,
  testConnector,
  updateConnector,
  type ConnectorProvider,
  type ConnectorStatus,
  type OutboundConnector,
  type OutboundConnectorCreate,
  type OutboundConnectorPatch,
  type TestConnectorResult,
} from '../api/connectors.js';
import { ApiError } from '../api/apiClient.js';
import { useUiStore } from '../state/uiStore.js';
import { t } from '../i18n/es.js';
import { getProviderWebhook, setProviderWebhook } from '../api/providerWebhook.js';

const PROVIDERS: ConnectorProvider[] = [
  'meta_native',
  '360dialog',
  'twilio',
  'messagebird',
];
const STATUSES: ConnectorStatus[] = ['connected', 'error'];
const META_LIKE = new Set<ConnectorProvider>(['meta_native', '360dialog']);

interface FormValues {
  name: string;
  provider: ConnectorProvider;
  businessPhone: string;
  wabaId: string;
  phoneNumberId: string;
  displayPhoneNumber: string;
  verifiedName: string;
  /** #159 — Credencial plain text (write-only). Se nombra "Access Token"
   * para meta_native y "API Key" para 360dialog/twilio/messagebird. */
  credential: string;
  status: ConnectorStatus;
}

const emptyValues: FormValues = {
  name: '',
  provider: 'meta_native',
  businessPhone: '',
  wabaId: '',
  phoneNumberId: '',
  displayPhoneNumber: '',
  verifiedName: '',
  credential: '',
  status: 'connected',
};

export const connectorFormSchema = z.object({
  name: z.string().trim().min(1).max(120),
  provider: z.enum(['meta_native', '360dialog', 'twilio', 'messagebird']),
  businessPhone: z.string().optional(),
  wabaId: z.string().optional(),
  phoneNumberId: z.string().optional(),
  displayPhoneNumber: z.string().optional(),
  verifiedName: z.string().optional(),
  // #159: en create requerido; en edit opcional (rota si llega).
  credential: z.string().optional(),
  status: z.enum(['connected', 'error']),
});

function valuesToCreate(v: FormValues): OutboundConnectorCreate {
  const out: OutboundConnectorCreate = {
    name: v.name.trim(),
    provider: v.provider,
  };
  if (v.status) out.status = v.status;
  // #159: credencial plain (write-only). meta_native usa `accessToken`,
  // resto usa `apiKey`. Backend provisiona Secret SM automáticamente.
  if (v.credential) {
    if (v.provider === 'meta_native') out.accessToken = v.credential;
    else out.apiKey = v.credential;
  }
  if (v.businessPhone) out.businessPhone = v.businessPhone;
  if (v.displayPhoneNumber) out.displayPhoneNumber = v.displayPhoneNumber;
  if (META_LIKE.has(v.provider)) {
    if (v.wabaId) out.wabaId = v.wabaId;
    if (v.phoneNumberId) out.phoneNumberId = v.phoneNumberId;
    if (v.verifiedName) out.verifiedName = v.verifiedName;
  }
  return out;
}

function buildPatch(
  current: OutboundConnector,
  v: FormValues,
): OutboundConnectorPatch {
  const patch: OutboundConnectorPatch = {};
  const trimmedName = v.name.trim();
  if (trimmedName !== current.name) patch.name = trimmedName;
  if (v.status !== current.status) patch.status = v.status;
  // #159 — rotación: si llega credencial, backend hace PutSecretValue.
  if (v.credential) {
    if (v.provider === 'meta_native') patch.accessToken = v.credential;
    else patch.apiKey = v.credential;
  }
  if ((v.businessPhone || undefined) !== (current.businessPhone ?? undefined)) {
    patch.businessPhone = v.businessPhone || undefined;
  }
  if (
    (v.displayPhoneNumber || undefined) !== (current.displayPhoneNumber ?? undefined)
  ) {
    patch.displayPhoneNumber = v.displayPhoneNumber || undefined;
  }
  if (META_LIKE.has(v.provider)) {
    if ((v.wabaId || undefined) !== (current.wabaId ?? undefined)) {
      patch.wabaId = v.wabaId || undefined;
    }
    if ((v.phoneNumberId || undefined) !== (current.phoneNumberId ?? undefined)) {
      patch.phoneNumberId = v.phoneNumberId || undefined;
    }
    if ((v.verifiedName || undefined) !== (current.verifiedName ?? undefined)) {
      patch.verifiedName = v.verifiedName || undefined;
    }
  }
  return patch;
}

export interface ConnectorFormProps {
  mode: 'create' | 'edit';
}

export function ConnectorForm({ mode }: ConnectorFormProps): JSX.Element {
  const { id = '', connectorId = '' } = useParams<{
    id: string;
    connectorId?: string;
  }>();
  const navigate = useNavigate();
  const pushToast = useUiStore((s) => s.pushToast);
  const queryClient = useQueryClient();

  const connectorQuery = useQuery({
    queryKey: ['connector', id, connectorId],
    queryFn: () => getConnector(id, connectorId),
    enabled: mode === 'edit' && Boolean(id && connectorId),
  });

  const defaultValues = useMemo<FormValues>(() => {
    if (mode === 'edit' && connectorQuery.data) {
      const c = connectorQuery.data;
      return {
        name: c.name,
        provider: c.provider,
        businessPhone: c.businessPhone ?? '',
        wabaId: c.wabaId ?? '',
        phoneNumberId: c.phoneNumberId ?? '',
        displayPhoneNumber: c.displayPhoneNumber ?? '',
        verifiedName: c.verifiedName ?? '',
        // #159: en edit, el credential SIEMPRE arranca vacío (write-only).
        // El connector ya tiene secret persistido; si se completa, se rota.
        credential: '',
        status: c.status,
      };
    }
    return emptyValues;
  }, [mode, connectorQuery.data]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const watchedProvider = watch('provider');

  const createMutation = useMutation({
    mutationFn: (body: OutboundConnectorCreate) => createConnector(id, body),
    onSuccess: () => {
      pushToast('success', t.connector.created);
      queryClient.invalidateQueries({ queryKey: ['connectors', id] });
      navigate(`/tenants/${id}/connectors`);
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (patch: OutboundConnectorPatch) => {
      const current = connectorQuery.data;
      if (!current) throw new Error('connector_not_loaded');
      return updateConnector(id, current.id, patch, current.version);
    },
    onSuccess: () => {
      pushToast('success', t.connector.updated);
      queryClient.invalidateQueries({ queryKey: ['connectors', id] });
      queryClient.invalidateQueries({ queryKey: ['connector', id, connectorId] });
      navigate(`/tenants/${id}/connectors`);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.status === 412) {
        pushToast('error', t.connector.versionConflict);
        return;
      }
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  // #160 — Test connection inline (solo en mode='edit', cuando ya hay Secret persistido).
  const [testResult, setTestResult] = useState<TestConnectorResult | null>(null);
  const testMutation = useMutation({
    mutationFn: () => testConnector(id, connectorId),
    onSuccess: (res) => {
      setTestResult(res);
      pushToast(res.ok ? 'success' : 'error',
        res.ok
          ? `Conexión OK · ${res.templatesCount ?? 0} templates en el provider`
          : `Conexión falló: ${res.error ?? 'error'}`);
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      setTestResult({ ok: false, provider: watchedProvider, error: msg });
      pushToast('error', msg);
    },
  });

  // #165 — Webhook config (solo 360dialog en edit mode).
  const enable360Webhook = mode === 'edit' && watchedProvider === '360dialog' && Boolean(connectorId);
  const webhookQuery = useQuery({
    queryKey: ['webhook-config', id, connectorId],
    queryFn: () => getProviderWebhook(id, connectorId),
    enabled: enable360Webhook,
  });
  const [webhookDraft, setWebhookDraft] = useState<string>('');
  useEffect(() => {
    if (webhookQuery.data?.url) setWebhookDraft(webhookQuery.data.url);
  }, [webhookQuery.data]);
  const setWebhookMutation = useMutation({
    mutationFn: () => setProviderWebhook(id, connectorId, webhookDraft.trim()),
    onSuccess: () => {
      pushToast('success', 'Webhook configurado en 360dialog');
      webhookQuery.refetch();
    },
    onError: (e: unknown) => pushToast('error', e instanceof ApiError ? e.message : (e as Error).message),
  });

  const onSubmit = handleSubmit((values) => {
    const parsed = connectorFormSchema.safeParse(values);
    if (!parsed.success) {
      pushToast('error', parsed.error.issues[0]?.message ?? 'invalid');
      return;
    }
    if (mode === 'create') {
      createMutation.mutate(valuesToCreate(values));
    } else if (connectorQuery.data) {
      updateMutation.mutate(buildPatch(connectorQuery.data, values));
    }
  });

  if (mode === 'edit' && connectorQuery.isLoading) return <p>{t.app.loading}</p>;
  if (mode === 'edit' && connectorQuery.error) {
    return <p className="text-red-700">{(connectorQuery.error as Error).message}</p>;
  }

  const submitting = isSubmitting || createMutation.isPending || updateMutation.isPending;

  return (
    <div>
      <h2 className="mb-4 text-xl font-semibold">
        {mode === 'create' ? t.connector.newTitle : t.connector.editTitle}
      </h2>
      <form
        onSubmit={onSubmit}
        className="space-y-4 rounded border border-slate-200 bg-white p-5"
        noValidate
      >
        <Field label={t.connector.fields.name} error={errors.name?.message}>
          <input
            type="text"
            {...register('name', {
              required: t.connector.nameRequired,
              maxLength: { value: 120, message: t.connector.nameTooLong },
            })}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </Field>

        <Field label={t.connector.fields.provider} error={errors.provider?.message}>
          <select
            {...register('provider', { required: t.connector.providerRequired })}
            disabled={mode === 'edit'}
            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm disabled:bg-slate-100"
          >
            {PROVIDERS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </Field>

        {META_LIKE.has(watchedProvider) && (
          <>
            <Field label={t.connector.fields.wabaId} error={errors.wabaId?.message}>
              <input
                type="text"
                {...register('wabaId')}
                className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </Field>
            <Field
              label={t.connector.fields.phoneNumberId}
              error={errors.phoneNumberId?.message}
            >
              <input
                type="text"
                {...register('phoneNumberId')}
                className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </Field>
            <Field
              label={t.connector.fields.verifiedName}
              error={errors.verifiedName?.message}
            >
              <input
                type="text"
                {...register('verifiedName')}
                className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </Field>
          </>
        )}

        <Field
          label={t.connector.fields.businessPhone}
          error={errors.businessPhone?.message}
        >
          <input
            type="text"
            {...register('businessPhone')}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </Field>

        <Field
          label={t.connector.fields.displayPhoneNumber}
          error={errors.displayPhoneNumber?.message}
        >
          <input
            type="text"
            {...register('displayPhoneNumber')}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </Field>

        <Field
          label={
            watchedProvider === 'meta_native'
              ? mode === 'create'
                ? 'Access Token de Meta'
                : 'Nuevo Access Token (dejar vacío para no rotar)'
              : mode === 'create'
                ? 'API Key del provider'
                : 'Nueva API Key (dejar vacío para no rotar)'
          }
          error={errors.credential?.message}
          helper={
            mode === 'create'
              ? 'Se guarda como Secret en AWS Secrets Manager (path /zen/dev/connector-secrets/<tenantId>/<connectorId>). Nunca se devuelve al frontend.'
              : 'Si se completa, rota el contenido del Secret existente. El ARN no cambia.'
          }
        >
          <input
            type="password"
            autoComplete="new-password"
            placeholder={mode === 'create' ? '••••••••' : 'sin cambio'}
            {...register('credential', {
              validate: (v) => {
                if (mode === 'create' && (!v || v.length < 8)) {
                  return 'Credencial requerida (mínimo 8 chars)';
                }
                if (v && v.length < 8) return 'Mínimo 8 chars';
                if (v && v.length > 4096) return 'Máximo 4096 chars';
                return true;
              },
            })}
            className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs"
          />
        </Field>

        <Field label={t.connector.fields.status} error={errors.status?.message}>
          <select
            {...register('status')}
            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t.connector.status[s]}
              </option>
            ))}
          </select>
        </Field>

        {/* #160 — Test connection: solo en edit y para providers que soportan health (meta_native / 360dialog). */}
        {mode === 'edit' && (watchedProvider === 'meta_native' || watchedProvider === '360dialog') && (
          <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm">
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="font-medium text-slate-700">Probar conexión al provider</div>
                <div className="text-xs text-slate-500">
                  Llama a {watchedProvider === 'meta_native' ? 'Meta Graph' : '360dialog'} con la credencial guardada y lista los templates.
                </div>
              </div>
              <button
                type="button"
                onClick={() => testMutation.mutate()}
                disabled={testMutation.isPending}
                className="rounded border border-slate-300 bg-white px-3 py-1 text-sm hover:bg-slate-100 disabled:opacity-50"
              >
                {testMutation.isPending ? 'Probando…' : 'Probar'}
              </button>
            </div>
            {testResult && (
              <div
                className={`mt-2 rounded px-2 py-1 text-xs ${
                  testResult.ok
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
                role="status"
              >
                {testResult.ok
                  ? `✓ Conexión OK · ${testResult.templatesCount ?? 0} templates`
                  : `✗ ${testResult.error ?? 'Falló'}`}
              </div>
            )}
          </div>
        )}

        {/* #165 — Webhook config: solo en edit y solo para 360dialog. */}
        {enable360Webhook && (
          <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm">
            <div className="mb-2 font-medium text-slate-700">Webhook 360dialog</div>
            <div className="mb-2 text-xs text-slate-500">
              URL pública a la que 360dialog enviará los eventos inbound (POST messages).
              Debe ser HTTPS. El servidor refresca la URL en cada save.
            </div>
            {webhookQuery.isLoading ? (
              <p className="text-xs text-slate-500">Consultando 360dialog…</p>
            ) : webhookQuery.error ? (
              <p className="text-xs text-red-700">{(webhookQuery.error as Error).message}</p>
            ) : (
              <>
                <div className="text-xs text-slate-600 mb-2">
                  Actual:{' '}
                  {webhookQuery.data?.url
                    ? <span className="font-mono">{webhookQuery.data.url}</span>
                    : <span className="text-slate-400 italic">sin configurar</span>}
                </div>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={webhookDraft}
                    onChange={(e) => setWebhookDraft(e.target.value)}
                    placeholder="https://inbound.dev.zen.zervizdev.com/v1/webhooks/360dialog"
                    className="flex-1 rounded border border-slate-300 px-2 py-1 font-mono text-xs"
                  />
                  <button
                    type="button"
                    onClick={() => setWebhookMutation.mutate()}
                    disabled={
                      setWebhookMutation.isPending ||
                      !webhookDraft.trim() ||
                      !webhookDraft.startsWith('https://')
                    }
                    className="rounded bg-brand-500 px-3 py-1 text-xs text-white hover:bg-brand-600 disabled:opacity-50"
                  >
                    {setWebhookMutation.isPending ? 'Guardando…' : 'Guardar webhook'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Link
            to={`/tenants/${id}/connectors`}
            className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-100"
          >
            {t.app.cancel}
          </Link>
          <button
            type="submit"
            disabled={submitting}
            className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {submitting ? '…' : t.app.save}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  error,
  helper,
  children,
}: {
  label: string;
  error?: string;
  helper?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-slate-700">{label}</span>
      {children}
      {helper && !error && <span className="mt-1 block text-xs text-slate-500">{helper}</span>}
      {error && (
        <span role="alert" className="mt-1 block text-xs text-red-600">
          {error}
        </span>
      )}
    </label>
  );
}
